import { FC, useEffect, useState, useCallback } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { MainLayout } from '../MainLayout';
import { DataTable } from 'src/components/table/DataTable';
import { Row, SortingState } from '@tanstack/react-table';
import { trpc } from 'src/trpc';
import { useSearchParams } from 'react-router-dom';
import { Loader } from 'src/components/Loader';
import { useOrganization } from 'src/context/OrganizationContext';
import { LocationDrawer } from './LocationSheet';
import { LocationColumns } from './LocationColumns';
import type { Location } from 'src/trpc';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { useToast } from 'src/components/useToast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LocationCreate } from '@libs/schemas';
import { Search } from 'src/components/Search';

export const LocationDashboard: FC = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [params, setParams] = useSearchParams({
    offset: '0',
    sortDirection: 'asc',
    sortKey: '',
    search: '',
  });
  const offset = parseInt(params.get('offset')!);
  const limit = 10;

  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  // Use organization context for org-dependent queries
  const { organizationId, isResolved, isOrganizationSwitching, setIsOrganizationSwitching } =
    useOrganization();

  // Search state management following other dashboard patterns
  const [search, setSearch] = useState('');

  const { data, isPending, isLoading, isError, isFetching } =
    trpc.admin.location.getAllLocations.useQuery(
      {
        limit,
        offset: offset,
        sortDirection: params.get('sortDirection') as 'asc' | 'desc',
        sortKey: params.get('sortKey')!,
        search: params.get('search') || '',
      },
      {
        placeholderData: keepPreviousData,
        enabled: isResolved && !!organizationId,
      },
    );

  // Reset organization switching state when fetch completes
  useEffect(() => {
    if (isOrganizationSwitching && !isFetching) {
      setIsOrganizationSwitching(false);
    }
  }, [isOrganizationSwitching, isFetching, setIsOrganizationSwitching]);

  useEffect(() => {
    if (sorting.length) {
      const newParams = new URLSearchParams(params);
      newParams.set('sortDirection', sorting[0].desc ? 'desc' : 'asc');
      newParams.set('sortKey', sorting[0].id);
      newParams.set('offset', '0');
      setParams(newParams);
    }
  }, [sorting, params, setParams]);

  useEffect(() => {
    params.set('search', search || '');
    params.set('offset', '0');
    setParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handlePageChange = (change: -1 | 1) => {
    const newOffset = Math.max(0, offset + change * limit);
    const newParams = new URLSearchParams(params);
    newParams.set('offset', newOffset.toString());
    setParams(newParams);
  };

  const handleRowClick = (location: Location) => {
    setSelectedLocation(location);
    setIsDrawerOpen(true);
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);

  const { successToast, errorToast } = useToast();
  const utils = trpc.useUtils();

  const deleteLocationMutation = trpc.admin.location.deleteLocation.useMutation({
    onSuccess: () => {
      successToast({
        title: 'Success',
        description: 'Location deleted successfully',
      });
      utils.admin.location.getAllLocations.invalidate();
      setLocationToDelete(null);
    },
    onError: (error) => {
      errorToast({
        title: 'Error',
        description: error.message || 'Failed to delete location',
      });
    },
  });

  const handleEditLocation = useCallback((location: Location) => {
    setSelectedLocation(location);
    setIsDrawerOpen(true);
  }, []);

  const handleCreateLocation = useCallback(() => {
    setSelectedLocation(null);
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedLocation(null);
  }, []);

  const handleDeleteLocation = useCallback((location: Location) => {
    setLocationToDelete(location);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!locationToDelete) return;

    await deleteLocationMutation.mutateAsync(locationToDelete._id!);
  }, [locationToDelete, deleteLocationMutation]);

  const columns = LocationColumns.map((column) => {
    if (column.id === 'actions') {
      return {
        ...column,
        cell: ({ row }: { row: Row<Location> }) => (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => handleEditLocation(row.original)}>
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteLocation(row.original)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      };
    }
    return column;
  });

  if (!isResolved || isOrganizationSwitching || (isPending && isFetching)) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-96">
          <Loader title="Getting Locations" />
        </div>
      </MainLayout>
    );
  }

  if (isError || !data) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <h2 className="text-xl font-semibold text-red-800">Error loading locations</h2>
          <p className="mt-2 text-gray-600">
            Please try again later or contact support if the issue persists.
          </p>
        </div>
      </MainLayout>
    );
  }

  const locations = data?.locations || [];
  const total = data?.total || 0;
  const pageCount = Math.ceil(total / limit);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-row justify-between">
          <h1 className="flex-1 text-3xl font-bold">Locations</h1>
          <Button onClick={handleCreateLocation} className="bg-primary hover:bg-primary/90">
            <Plus className="mr-2 h-4 w-4" />
            Create New Location
          </Button>
        </div>

        <Search
          value={search}
          onChange={(value) => setSearch(value)}
          placeholder="Search locations by name or code"
          isLoading={isLoading}
        />

        <DataTable
          total={total}
          resourceName="locations"
          offset={offset}
          limit={limit}
          columns={columns}
          data={locations}
          onRowClick={handleRowClick}
          sorting={sorting}
          onSortingChange={setSorting}
          hasNextPage={currentPage < pageCount}
          hasPreviousPage={currentPage > 1}
          onPageChange={handlePageChange}
          pageCount={pageCount}
          currentPage={currentPage}
        />

        {/* Yard Drawer */}
        <LocationDrawer
          location={selectedLocation as LocationCreate}
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          onSuccess={() => {
            // Data will be refreshed by the mutation
          }}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!locationToDelete} onOpenChange={() => setLocationToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Location</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{locationToDelete?.name}"? This action cannot be
                undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700"
                disabled={deleteLocationMutation.isPending}
              >
                {deleteLocationMutation.isPending ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
};
